<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const currentMode = (this.$route.query.mode || '').toLowerCase();
    if (currentMode === 'embedded') {
      // eslint-disable-next-line no-console
      console.log('🧘 Modo Zen Ativado: EMBEDDED');
    }
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.$store.dispatch('customViews/get', 'conversation');
    this.$store.dispatch('customViews/get', 'contact');

    // [VIGICAM] Navegação sem reload: escuta postMessage do sistema-vigi e troca
    // de conversa via vue-router (client-side), evitando o reboot da SPA no iframe.
    this.boundVigicamNavigate = this.handleVigicamNavigate.bind(this);
    window.addEventListener('message', this.boundVigicamNavigate);
  },
  unmounted() {
    if (this.boundVigicamNavigate) {
      window.removeEventListener('message', this.boundVigicamNavigate);
    }
  },
  methods: {
    // [VIGICAM] Recebe { type: 'VIGICAM_NAVIGATE', conversationId } do parent e
    // navega client-side para a conversa, sem recarregar o iframe (espelho de
    // entrada da "Ponte de Dados" em conversations/actions.js).
    handleVigicamNavigate(event) {
      const data = event?.data;
      if (!data || data.type !== 'VIGICAM_NAVIGATE') return;
      // Aceita apenas mensagens do sistema Vigi (ou localhost em desenvolvimento).
      if (
        event.origin &&
        !event.origin.includes('vigicameras.com.br') &&
        !event.origin.includes('localhost')
      ) {
        return;
      }
      const conversationId = parseInt(data.conversationId, 10);
      if (!conversationId || Number.isNaN(conversationId)) return;
      if (!this.accountId) return;
      // Evita navegação redundante para a mesma conversa.
      if (
        String(this.$route.params.conversation_id) === String(conversationId)
      ) {
        return;
      }
      const mode = (this.$route.query.mode || '').toLowerCase();
      this.$router
        .push({
          name: 'inbox_conversation',
          params: {
            accountId: String(this.accountId),
            conversation_id: String(conversationId),
          },
          query: mode ? { mode } : {},
        })
        .catch(() => {});
    },
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div
    class="flex flex-grow overflow-hidden text-n-slate-12"
    :class="{
      'is-embedded': ($route.query.mode || '').toLowerCase() === 'embedded',
      'is-cockpit': ($route.query.mode || '').toLowerCase() === 'cockpit',
      'is-chatsopen': ($route.query.mode || '').toLowerCase() === 'chatsopen',
    }"
  >
    <NextSidebar
      v-if="
        !['cockpit', 'embedded', 'chatsopen'].includes(
          ($route.query.mode || '').toLowerCase()
        )
      "
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          v-if="
            !['embedded', 'cockpit', 'chatsopen'].includes(
              ($route.query.mode || '').toLowerCase()
            )
          "
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar v-if="$route.query.mode !== 'embedded'" />
        <CopilotLauncher v-if="$route.query.mode !== 'embedded'" />
        <MobileSidebarLauncher
          v-if="
            !['embedded', 'cockpit', 'chatsopen'].includes(
              ($route.query.mode || '').toLowerCase()
            )
          "
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer v-if="$route.query.mode !== 'embedded'" />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
